import React, { useEffect, useMemo, useState } from 'react';
import { YesNAAgent } from '@yesstock/yeslang-chat-ui-v2';
import type { AgentConfig } from '@yesstock/yeslang-chat-ui-v2';
import '@yesstock/yeslang-chat-ui-v2/dist/styles/YesLangChat.css';
import './App.css';

/** 호스트(Thymeleaf)가 주입하는 CHAT_CONFIG — YesNAAgent 브릿지 */
declare global {
  interface Window {
    CHAT_CONFIG?: {
      apiEndpoint?: string;
      sessionId?: string;
      userId?: string;
      theme?: 'light' | 'dark';
      customColors?: any;
      userNickname?: string;
      welcomeHtml?: string;
      /** AgentSelector 하단 FREE패스 등 프로모 슬롯 */
      promoHtml?: string;
      sidebarBottomContent?: React.ReactNode;
      sidebarBottomHtml?: string;
      chatTermsAgreed?: boolean;
      onAuthRequired?: (reason: 'login' | 'terms') => void;
      onCreditCharge?: () => void;
      isFirstUser?: boolean;
      onMessage?: (message: any) => void;
      onChatPollingEvent?: () => void;
      serviceName?: string;
      showModelSettings?: boolean;
      showVoiceInput?: boolean;
      showInputImage?: boolean;
      /** YesNAAgent agents (yeshome: yl + ys). playground 미사용 */
      agents?: AgentConfig[];
    };
  }
}

function buildAgentsFromConfig(config: NonNullable<Window['CHAT_CONFIG']>): AgentConfig[] {
  if (Array.isArray(config.agents) && config.agents.length > 0) {
    // playground는 yeshome에서 미사용 — 전달돼도 제거
    return config.agents.map((a) => {
      const { playground: _omit, ...rest } = a as AgentConfig & { playground?: unknown };
      return rest;
    });
  }

  // 하위 호환: agents 미지정 시 apiEndpoint → yl 단독
  const ylEndpoint = config.apiEndpoint || '';
  return [
    {
      agentType: 'yl',
      endPoint: ylEndpoint,
      mode: 'show',
      showChartChip: false,
      showInputImage: config.showInputImage ? 1 : undefined,
    },
    {
      agentType: 'ys',
      endPoint: '',
      mode: 'disable',
    },
  ];
}

function App() {
  const [chatConfig, setChatConfig] = useState<Window['CHAT_CONFIG'] | null>(null);

  useEffect(() => {
    const config = window.CHAT_CONFIG || {};
    setChatConfig({
      apiEndpoint: config.apiEndpoint || '',
      sessionId: config.sessionId,
      userId: config.userId,
      theme: config.theme || 'light',
      customColors: config.customColors,
      userNickname: config.userNickname,
      welcomeHtml: config.welcomeHtml,
      promoHtml: config.promoHtml,
      sidebarBottomContent: config.sidebarBottomContent,
      sidebarBottomHtml: config.sidebarBottomHtml,
      chatTermsAgreed: config.chatTermsAgreed,
      onAuthRequired: config.onAuthRequired,
      onCreditCharge: config.onCreditCharge,
      isFirstUser: config.isFirstUser,
      onMessage: config.onMessage,
      onChatPollingEvent: config.onChatPollingEvent,
      serviceName: config.serviceName,
      showModelSettings: config.showModelSettings,
      showVoiceInput: config.showVoiceInput,
      showInputImage: config.showInputImage,
      agents: config.agents,
    });
  }, []);

  const agents = useMemo(
    () => (chatConfig ? buildAgentsFromConfig(chatConfig) : []),
    [chatConfig],
  );

  if (!chatConfig) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <YesNAAgent
        agents={agents}
        userId={chatConfig.userId}
        theme={chatConfig.theme}
        customColors={chatConfig.customColors}
        userNickname={chatConfig.userNickname}
        sidebarBottomContent={chatConfig.sidebarBottomContent}
        sidebarBottomHtml={chatConfig.sidebarBottomHtml}
        promoHtml={chatConfig.promoHtml}
        chatTermsAgreed={chatConfig.chatTermsAgreed}
        onAuthRequired={(reason) => window.CHAT_CONFIG?.onAuthRequired?.(reason)}
        onCreditCharge={
          chatConfig.onCreditCharge
            ? () => window.CHAT_CONFIG?.onCreditCharge?.()
            : undefined
        }
        isFirstUser={chatConfig.isFirstUser}
        onChatPollingEvent={() => chatConfig.onChatPollingEvent?.()}
        serviceName={chatConfig.serviceName}
        showModelSettings={chatConfig.showModelSettings}
        showVoiceInput={chatConfig.showVoiceInput}
        onError={(error) => {
          console.error('Chat error:', error);
        }}
      />
    </div>
  );
}

export default App;
